/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    runTransaction,
    writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Facture, factureStatut } from "./facture.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { handleRelanceUser } from "../../../services/firebaseService";


// Numéro de facture séquentiel par année et par créateur (AAAA-XXXX).
// Utilisé à la fois par la création manuelle (AddOrUpdateFactureModal) et
// par l'import en masse (UseImportFactureForm) : dans ce dernier cas,
// l'appelant doit ré-incrémenter la séquence localement pour chaque ligne
// du fichier plutôt que de rappeler cette fonction en boucle (elle ne voit
// que ce qui est déjà en base).
export const generateNextFactureNumero = async (createdBy: string) => {
    const annee = new Date().getFullYear().toString();
    const facturesRef = collection(db, "factures");

    const q = query(
        facturesRef,
        where("createdBy", "==", createdBy),
        where("numeroFacture", ">=", `${annee}-0000`),
        where("numeroFacture", "<=", `${annee}-9999`),
        orderBy("numeroFacture", "desc"),
        limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
        return `${annee}-0001`;
    }

    const dernierNumero = snapshot.docs[0].data().numeroFacture as string;
    const dernierSequence = parseInt(dernierNumero.split("-")[1], 10);
    const prochainSequence = dernierSequence + 1;

    return `${annee}-${prochainSequence.toString().padStart(4, "0")}`;
};

// Incrémente localement un numéro "AAAA-XXXX" déjà généré, sans requête
// Firestore : utilisé par l'import en masse pour attribuer un numéro
// unique à chaque ligne du fichier sans réinterroger la base à chaque tour.
const incrementFactureNumero = (numero: string) => {
    const [annee, sequence] = numero.split("-");
    const prochaine = (parseInt(sequence, 10) + 1).toString().padStart(4, "0");
    return `${annee}-${prochaine}`;
};

export type ImportFactureRow = {
    studentId: string;
    intitule: string;
    montant: number;
    devise: string;
    dateLimite: string;
};

export const FactureApi = createApi({
    reducerPath: "factureApi",
    tagTypes: ["facture", "factures", "facturesByProf", "facturesByStudent", "relanceUserFacture"],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditFacture: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        // const students: string[] = data.students ?? [];
                        // const { students: _, ...dataWithoutStudents } = data;

                        // const docRefs = await Promise.all(
                        //     students.map((studentId: string) =>
                        //         addDoc(collection(db, "factures"), {
                        //             ...dataWithoutStudents, 
                        //             studentId,
                        //             createdAt: serverTimestamp(), 
                        //             createdBy: userId, 
                        //             updatedAt: serverTimestamp() 
                        //         })
                        //     )
                        // );

                        const docRef = await addDoc(collection(db, "factures"), {
                            ...data,
                            createdAt: serverTimestamp(),
                            createdBy: userId,
                            updatedAt: serverTimestamp()
                        });


                        return {
                            data: {
                                id: docRef.id,
                                message: "Facture créée !",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        // const students: string[] = data.students ?? [];
                        // const { students: _, ...dataWithoutStudents } = data;

                        const compRef = doc(db, "factures", id);
                        await updateDoc(compRef, {
                            ...data,
                            updatedAt: serverTimestamp()
                        });

                        return {
                            data: {
                                message:
                                    "Facture modifiée !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["facture", "factures", 'facturesByStudent', 'facturesByProf'],
        }),
        // Import en masse : les lignes reçues ici sont déjà validées et
        // résolues côté formulaire (élève retrouvé parmi ceux du prof,
        // montant/date vérifiés) - cet endpoint ne fait que l'écriture
        // Firestore, en un seul batch, avec un numéro de facture
        // séquentiel généré localement pour chaque ligne.
        importFactures: builder.mutation({
            async queryFn(rows: ImportFactureRow[]) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    if (!rows?.length) {
                        return { data: { success: false, imported: 0, message: "Aucune ligne valide à importer." } };
                    }
                    const userId = user.uid;

                    let numeroFacture = await generateNextFactureNumero(userId);
                    const batch = writeBatch(db);

                    for (const row of rows) {
                        const ref = doc(collection(db, "factures"));
                        batch.set(ref, {
                            studentId: row.studentId,
                            numeroFacture,
                            intitule: row.intitule,
                            montant: row.montant,
                            devise: row.devise,
                            statut: "not_paid",
                            dateLimite: row.dateLimite,
                            createdAt: serverTimestamp(),
                            createdBy: userId,
                            updatedAt: serverTimestamp(),
                        });
                        numeroFacture = incrementFactureNumero(numeroFacture);
                    }

                    await batch.commit();

                    return {
                        data: {
                            success: true,
                            imported: rows.length,
                            message: `${rows.length} facture(s) importée(s) avec succès.`,
                        },
                    };
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["facture", "factures", 'facturesByStudent', 'facturesByProf'],
        }),
        getFactures: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "factures"),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const Factures = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: Factures as Facture[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const facturesRef = query(
                    collection(db, "factures"),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(facturesRef, (snapshot) => {
                        const factures = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => factures as Facture[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["factures"],
        }),
        getFacturesByProf: builder.query({
            async queryFn() {
                return { data: [] };
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                    await cacheDataLoaded;

                    const user = auth.currentUser;
                    if (!user) return;

                    const q = query(
                        collection(db, "factures"),
                        where("createdBy", "==", user.uid),
                        orderBy("createdAt", "desc")
                    );

                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const allFactures = await Promise.all(
                            snapshot.docs.map(async (d) => {
                                const facture = { id: d.id, ...d.data() } as any;

                                // Student
                                if (facture.studentId) {
                                    try {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", facture.studentId)
                                        );
                                        facture.studentData = studentSnap.exists()
                                            ? studentSnap.data()
                                            : facture.studentId;
                                    } catch { }
                                }

                                // Prestataire
                                if (facture.createdBy) {
                                    try {
                                        const prestataireSnap = await getDoc(
                                            doc(db, "users", facture.createdBy)
                                        );
                                        facture.prestataireData = prestataireSnap.exists()
                                            ? prestataireSnap.data()
                                            : facture.createdBy;
                                    } catch { }
                                }

                                // Payments
                                try {
                                    const paymentsSnap = await getDocs(
                                        query(
                                            collection(db, "paymentFactures"),
                                            where("factureId", "==", facture.id),
                                            orderBy("createdAt", "asc")
                                        )
                                    );
                                    facture.payments = paymentsSnap.docs.map((d) => ({
                                        id: d.id,
                                        ...d.data(),
                                    }));
                                } catch (error) {
                                    console.error("Erreur payments pour", facture.id, error);
                                    facture.payments = [];
                                }

                                return facture;
                            })
                        );

                        updateCachedData(() => allFactures as any);
                    });

                } catch {
                    // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["facturesByProf"],
        }),
        getFactureById: builder.query({
            queryFn: async (FactureId) => {
                try {
                    const FactureRef = doc(db, "factures", FactureId);
                    const FactureSnap = await getDoc(FactureRef);

                    if (!FactureSnap.exists()) {
                        return {
                            error: { status: 404, message: "Évaluation introuvable !" },
                        };
                    }

                    return { data: { id: FactureSnap.id, ...FactureSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["facture"],
        }),
        getFacturesByStudent: builder.query({

            async queryFn(studentId: string) {

                if (!studentId) return { error: { message: "studentId manquant" } };

                try {

                    const q = query(
                        collection(db, "factures"),
                        where("studentId", "==", studentId),
                        orderBy("createdAt", "desc")
                    );


                    const snapshot = await getDocs(q);

                    const factures = snapshot.docs.map((doc) => ({
                        uid: doc.id,
                        ...doc.data(),
                    })) as any[];


                    const facturesWithStudent = await Promise.all(
                        factures.map(async (facture) => {
                            if (!facture.studentId) return facture;
                            try {
                                const studentRef = doc(db, "users", facture.studentId);
                                const studentSnap = await getDoc(studentRef);
                                return {
                                    ...facture,
                                    studentData: studentSnap.exists()
                                        ? { uid: studentSnap.id, ...studentSnap.data() }
                                        : facture.studentId,
                                };
                            } catch {
                                return facture;
                            }
                        })
                    );

                    return { data: facturesWithStudent };
                } catch (error) {
                    console.error("ERREUR queryFn:", error);
                    return { error };
                }
            },

            keepUnusedDataFor: 60,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                    await cacheDataLoaded;


                    const q = query(
                        collection(db, "factures"),
                        where("studentId", "==", arg),
                        orderBy("createdAt", "desc")
                    );

                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const factures = snapshot.docs.map((d) => ({
                            uid: d.id,
                            ...d.data(),
                        })) as any[];

                        const facturesWithStudent = await Promise.all(
                            factures.map(async (facture) => {
                                if (!facture.studentId) return facture;
                                try {
                                    const studentRef = doc(db, "users", facture.studentId);
                                    const studentSnap = await getDoc(studentRef);
                                    return {
                                        ...facture,
                                        studentData: studentSnap.exists()
                                            ? { uid: studentSnap.id, ...studentSnap.data() }
                                            : facture.studentId,
                                    };
                                } catch {
                                    return facture;
                                }
                            })
                        );

                        updateCachedData(() => facturesWithStudent);
                    });

                } catch {
                    // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe(); // nettoyage du listener
            },

            providesTags: ["facturesByStudent"],
        }),
        // Supprimer
        deleteFacture: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "factures", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Facture supprimée !", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["facture", "factures", 'facturesByStudent', 'facturesByProf'],
        }),
        relanceUserFacture: builder.mutation({
            async queryFn({ email, fullName, message, factureId }) {

                try {

                    const response = await handleRelanceUser({ email, fullName, message, factureId });
                    return {
                        data: response,
                        message: "Relance envoyée par mail.",
                    };

                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["relanceUserFacture"],
        }),

        addPaymentFacture: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {

                        // 1. Ajouter le paiement
                        const docRef = await addDoc(collection(db, "paymentFactures"), {
                            ...data,
                            createdAt: serverTimestamp(),
                            createdBy: userId,
                            updatedAt: serverTimestamp()
                        });

                        // 2. Récupérer la facture actuelle
                        const factureRef = doc(db, "factures", data.factureId);
                        const factureSnap = await getDoc(factureRef);
                        const facture = factureSnap.data() as Facture;

                        // 3. Calculer le nouveau montantVerse (cumul)
                        const nouveauMontantVerse = (facture.montantVerse || 0) + data.montant;

                        // 4. Déterminer le statut
                        let statut: factureStatut;
                        if (nouveauMontantVerse <= 0) {
                            statut = "not_paid";
                        } else if (nouveauMontantVerse < facture.montant) {
                            statut = "partially_paid";
                        } else {
                            statut = "paid";
                        }

                        // 5. Mettre à jour la facture
                        await updateDoc(factureRef, {
                            montantVerse: nouveauMontantVerse,
                            statut,
                            datePaiement: data.datePaiement,  // date du dernier paiement ajouté
                            updatedAt: serverTimestamp()
                        });

                        return {
                            data: {
                                id: docRef.id,
                                message: "Paiement ajouté.",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore

                        const compRef = doc(db, "paymentFactures", id);
                        await updateDoc(compRef, {
                            ...data,
                            updatedAt: serverTimestamp()
                        });

                        return {
                            data: {
                                message:
                                    "Paiement modifié.",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["facture", "factures", "facturesByProf", "facturesByStudent",],
        }),

    }),
});

export const {
    useAddOrEditFactureMutation,
    useImportFacturesMutation,
    useGetFacturesQuery,
    useDeleteFactureMutation,
    useLazyGetFactureByIdQuery,
    useGetFacturesByStudentQuery,
    useGetFacturesByProfQuery,
    useRelanceUserFactureMutation,
    useAddPaymentFactureMutation,
} = FactureApi;

export function useFactureFromLocation(): [
    Facture,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Facture | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetFactureByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Facture);
        }
    }, [data]);

    return [item as Facture & { bio: string }, isLoading, id as string, findById];
}



